import {createAsyncThunk} from '@reduxjs/toolkit'
import axios from 'axios'
import {
  zadCatalogueByIdUrl,
  zadCatalogueByProductUrl,
  zadCatalogueImageRemoveUrl,
  zadCatalogueImageUrl,
  zadCatalogueStatusUrl,
  zadCatalogueUpdateUrl,
  zadCatalogueUrl,
} from '../../api'
import {buildZadListQuery} from '../zad/query'
import {parseZadApiError} from '../zad/errors'
import {
  extractCatalogueItem,
  extractCatalogueList,
  getCatalogueProductId,
  sanitizeCatalogueWritePayload,
} from '../zad/catalogueHelpers'
import {
  CreateZadCatalogueRequest,
  FetchZadCatalogueByIdRequest,
  FetchZadCatalogueByProductRequest,
  FetchZadCatalogueRequest,
  RemoveZadCatalogueImageRequest,
  UpdateZadCatalogueRequest,
  UpdateZadCatalogueStatusRequest,
  UploadZadCatalogueImageRequest,
} from './types'

function catalogueResult(data: unknown) {
  const item = extractCatalogueItem(data)
  return {
    success: true as const,
    item,
  }
}

export async function fetchConfiguredCatalogueProductIds(): Promise<Set<string>> {
  const ids = new Set<string>()
  let page = 1
  let pages = 1
  do {
    const res = await axios.get(
      `${zadCatalogueUrl}?${buildZadListQuery({pageNumber: page, pageCount: 100})}`
    )
    const list = extractCatalogueList(res.data || {})
    list.items.forEach((item) => {
      const productId = getCatalogueProductId(item)
      if (productId) ids.add(productId)
    })
    pages = list.totalPages > 0 ? list.totalPages : 1
    page += 1
  } while (page <= pages && page <= 20)
  return ids
}

export const fetchZadCatalogue = createAsyncThunk(
  'zadCatalogue/fetch',
  async (payload: FetchZadCatalogueRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(`${zadCatalogueUrl}?${buildZadListQuery(payload)}`)
      const list = extractCatalogueList(res.data || {})
      return {
        success: true as const,
        items: list.items,
        total: list.total,
        totalPages: list.totalPages,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadCatalogueById = createAsyncThunk(
  'zadCatalogue/fetchById',
  async (payload: FetchZadCatalogueByIdRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(zadCatalogueByIdUrl(payload.id))
      return catalogueResult(res.data)
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadCatalogueByProduct = createAsyncThunk(
  'zadCatalogue/fetchByProduct',
  async (payload: FetchZadCatalogueByProductRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(zadCatalogueByProductUrl(payload.productId))
      return catalogueResult(res.data)
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const createZadCatalogueConfig = createAsyncThunk(
  'zadCatalogue/create',
  async (payload: CreateZadCatalogueRequest, {rejectWithValue}) => {
    try {
      const body = {
        productId: payload.productId,
        isPublished: false,
        publicDescription: payload.publicDescription || '',
        isFeatured: payload.isFeatured === true,
        displayOrder: typeof payload.displayOrder === 'number' ? payload.displayOrder : 0,
      }
      const res = await axios.post(zadCatalogueUrl, body)
      return catalogueResult(res.data)
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const updateZadCatalogueConfig = createAsyncThunk(
  'zadCatalogue/update',
  async (payload: UpdateZadCatalogueRequest, {rejectWithValue}) => {
    try {
      const {id, ...values} = payload
      const body = sanitizeCatalogueWritePayload(values)
      const res = await axios.post(zadCatalogueUpdateUrl(id), body)
      return catalogueResult(res.data)
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const updateZadCatalogueStatus = createAsyncThunk(
  'zadCatalogue/updateStatus',
  async (payload: UpdateZadCatalogueStatusRequest, {rejectWithValue}) => {
    try {
      const res = await axios.post(zadCatalogueStatusUrl(payload.id), {
        isPublished: payload.isPublished,
      })
      return catalogueResult(res.data)
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const uploadZadCatalogueImage = createAsyncThunk(
  'zadCatalogue/uploadImage',
  async (payload: UploadZadCatalogueImageRequest, {rejectWithValue}) => {
    try {
      const data = new FormData()
      data.append('image', payload.file)
      const res = await axios.post(zadCatalogueImageUrl(payload.id), data, {
        headers: {'Content-Type': 'multipart/form-data'},
      })
      return catalogueResult(res.data)
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const removeZadCatalogueImage = createAsyncThunk(
  'zadCatalogue/removeImage',
  async (payload: RemoveZadCatalogueImageRequest, {rejectWithValue}) => {
    try {
      const res = await axios.post(zadCatalogueImageRemoveUrl(payload.id), {})
      return catalogueResult(res.data)
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)
